import { lazy, ComponentType } from 'react';
import { log } from '@/lib/logger';
import { safeParseInt } from '@/lib/numberHelpers';

/**
 * Storage keys to track chunk reload attempts.
 * Uses localStorage (not sessionStorage) because iOS Safari may not flush
 * sessionStorage writes that occur immediately before window.location.href
 * navigation, causing the cooldown marker to be lost on that platform.
 */
const CHUNK_RELOAD_KEY = 'nynch_chunk_reload_attempted';
const CHUNK_RELOAD_TIMESTAMP_KEY = 'nynch_chunk_reload_timestamp';
const CHUNK_RELOAD_COUNTER_KEY = 'nynch_chunk_reload_count';
const RELOAD_COOLDOWN_MS = 10_000;       // 10 seconds cooldown between auto-reloads
const MAX_AUTO_RELOADS = 2;              // Hard cap on total auto-reloads
const MARKER_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes — markers expire after this

/**
 * Checks if an error is a chunk/module load error.
 * These occur when:
 * 1. The app was deployed with new chunks
 * 2. User has cached HTML pointing to old chunk filenames
 * 3. Browser tries to fetch non-existent chunk file
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  return (
    // Vite/ES modules error messages
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    // Webpack-style error
    name.includes('chunkloaderror') ||
    // Network errors during chunk load
    (message.includes('failed to fetch') && message.includes('/assets/')) ||
    // TypeScript error for missing module
    message.includes('cannot find module')
  );
}

/**
 * Detects the "module resolved to undefined" failure mode, where a dynamic
 * import settles successfully but the module namespace (or its expected export)
 * is missing. The lazy import then throws a generic TypeError from its
 * `.then(m => ({ default: m.SomeExport }))` mapping —
 * "Cannot read properties of undefined (reading 'SomeExport')".
 *
 * In practice this is a stale/corrupted-chunk symptom (a cached HTML shell
 * served in place of a deployed-away chunk, or a browser/extension intercepting
 * the module fetch), not an application bug. It is therefore routed into the
 * same auto-reload recovery as a chunk load error instead of hard-crashing to
 * the ErrorBoundary.
 *
 * Inside `lazyWithRetry`'s catch the only code that ran is the pure
 * import-and-map `importFn`, so the match is safe there. Reference: Sentry
 * NYNCH-78.
 *
 * Error boundaries also see this message from ordinary render crashes, so they
 * must use `isStaleVersionFailure`, which stops calling it an update once a
 * refresh has failed to clear it. They cannot use `isChunkLoadError` alone —
 * NYNCH-D3 reached Settings View because the TypeError bypassed the
 * "Update Available" UI after Vite preload had already started a reload.
 */
export function isUndefinedModuleError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;

  const message = error.message.toLowerCase();

  return (
    // V8 / Chromium
    message.includes('cannot read properties of undefined') ||
    message.includes('cannot read property') ||
    // Safari / WebKit
    message.includes('undefined is not an object')
  );
}

/** Stale-chunk failures: missing file *or* undefined module namespace. */
export function isRecoverableChunkFailure(error: unknown): boolean {
  return isChunkLoadError(error) || isUndefinedModuleError(error);
}

/**
 * Re-homes an undefined-module TypeError as a ChunkLoadError so error
 * boundaries treat it as a deploy artifact, not an application crash.
 * Leaves genuine chunk-load errors unchanged.
 */
export function asChunkLoadError(error: Error): Error {
  if (isChunkLoadError(error)) return error;
  const wrapped = new Error(error.message);
  wrapped.name = 'ChunkLoadError';
  // A module whose own top-level code throws the same TypeError also lands
  // here. Keep its original stack and the error itself so Sentry can still
  // tell a genuine evaluation bug from a stale chunk.
  wrapped.stack = error.stack;
  Object.assign(wrapped, { cause: error });
  return wrapped;
}

/**
 * True when forceHardReload already scheduled navigation. The 50ms iOS
 * flush delay plus the upcoming unload is a race: a second lazy import
 * must wait, not throw into the section boundary (NYNCH-D3).
 */
export function isChunkReloadInFlight(now = Date.now()): boolean {
  const lastAttempt = localStorage.getItem(CHUNK_RELOAD_TIMESTAMP_KEY);
  const lastAttemptTime = lastAttempt ? safeParseInt(lastAttempt, 0) : 0;
  if (lastAttemptTime <= 0) return false;
  return (now - lastAttemptTime) < 2_000;
}

function getReloadCount(): number {
  const count = localStorage.getItem(CHUNK_RELOAD_COUNTER_KEY);
  return count ? safeParseInt(count, 10) : 0;
}

function incrementReloadCount(): void {
  const count = getReloadCount();
  localStorage.setItem(CHUNK_RELOAD_COUNTER_KEY, (count + 1).toString());
}

/**
 * Checks if we should attempt an auto-reload for a chunk error.
 * Returns false if the hard cap or time-based cooldown has been reached.
 *
 * Markers expire after MARKER_EXPIRY_MS so that genuinely new deployments
 * (hours later) get fresh retry attempts. This replaces the old approach of
 * clearing markers on `window.load` with a timer, which was the root cause of
 * infinite reload loops on slow mobile networks — the timer fired before lazy
 * chunks even started loading.
 */
export function shouldAutoReload(): boolean {
  const now = Date.now();
  const lastAttempt = localStorage.getItem(CHUNK_RELOAD_TIMESTAMP_KEY);
  const lastAttemptTime = lastAttempt ? safeParseInt(lastAttempt, 0) : 0;

  // If markers are older than MARKER_EXPIRY_MS, treat as a fresh session
  // (e.g., genuinely new deployment, user returning hours later).
  if (lastAttemptTime > 0 && (now - lastAttemptTime) > MARKER_EXPIRY_MS) {
    clearChunkReloadMarkers();
  }

  // Hard cap: never auto-reload more than MAX_AUTO_RELOADS times within the window
  if (getReloadCount() >= MAX_AUTO_RELOADS) {
    log.warn('Chunk reload cap reached, deferring to ErrorBoundary', {
      count: getReloadCount(),
      max: MAX_AUTO_RELOADS });
    return false;
  }

  // Cooldown: don't reload if we just reloaded
  if (lastAttemptTime > 0 && (now - lastAttemptTime) < RELOAD_COOLDOWN_MS) {
    log.warn('Chunk reload attempted recently, skipping auto-reload', {
      timeSinceLastAttempt: now - lastAttemptTime,
      cooldown: RELOAD_COOLDOWN_MS });
    return false;
  }

  return true;
}

/**
 * Marks that we've attempted a reload for chunk errors.
 */
function markReloadAttempted(): void {
  incrementReloadCount();
  localStorage.setItem(CHUNK_RELOAD_KEY, 'true');
  localStorage.setItem(CHUNK_RELOAD_TIMESTAMP_KEY, Date.now().toString());
}

/**
 * Clears the reload attempt markers (call this on successful page load).
 */
export function clearChunkReloadMarkers(): void {
  localStorage.removeItem(CHUNK_RELOAD_KEY);
  localStorage.removeItem(CHUNK_RELOAD_TIMESTAMP_KEY);
  localStorage.removeItem(CHUNK_RELOAD_COUNTER_KEY);
}

/**
 * Forces a hard reload to fetch fresh assets.
 * Uses cache-busting to ensure we get the latest version.
 * A 50ms delay before navigation ensures the localStorage write is committed
 * on iOS Safari before the page unloads.
 */
export function forceHardReload(): void {
  markReloadAttempted();

  // Try to clear caches if available
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name);
      });
    }).catch(() => {
      // Ignore cache deletion errors
    });
  }

  // Delay navigation by 50ms to guarantee the localStorage write above
  // is flushed to disk before the page unloads (iOS Safari guard).
  setTimeout(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('_reload', Date.now().toString());
    // Reload this entry so reviewed router handoffs survive asset recovery.
    window.history.replaceState(window.history.state, document.title, url.toString());
    window.location.reload();
  }, 50);
}

/**
 * Handles a chunk load error by attempting recovery.
 * 1. First attempts (up to MAX_AUTO_RELOADS): Auto-reload the page
 * 2. After cap is hit: Let error bubble up for ErrorBoundary to handle
 */
export function handleChunkLoadError(error: Error, modulePath: string): void {
  log.warn('Chunk load error detected', {
    error: error.message,
    modulePath,
    userAgent: navigator.userAgent });

  if (shouldAutoReload()) {
    log.info('Auto-reloading to fetch updated chunks', { modulePath });
    forceHardReload();
    return;
  }

  if (isChunkReloadInFlight()) {
    log.info('Chunk reload already in flight, waiting for navigation', { modulePath });
    return;
  }

  // Cap or cooldown with no in-flight reload — ErrorBoundary shows refresh UI
  throw asChunkLoadError(error);
}

/**
 * Creates a lazy component with automatic retry for chunk load errors.
 *
 * Usage:
 * ```typescript
 * const MyComponent = lazyWithRetry(
 *   () => import('./MyComponent').then(m => ({ default: m.MyComponent }))
 * );
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.lazy uses ComponentType<any> so propful lazy imports infer correctly.
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  moduleName?: string
): React.LazyExoticComponent<T> {
  return lazy(() => importWithChunkRetry(importFn, moduleName));
}

/**
 * Runs a lazy import and recovers stale-chunk failures. Exported so tests
 * can exercise the catch path without mounting React.lazy.
 */
export async function importWithChunkRetry<T>(
  importFn: () => Promise<{ default: T }>,
  moduleName?: string,
): Promise<{ default: T }> {
  try {
    return await importFn();
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));

    if (isRecoverableChunkFailure(err)) {
      handleChunkLoadError(err, moduleName || 'unknown');
      // Reload is scheduled or already in flight — stay pending until unload.
      return await new Promise<{ default: T }>(() => {
        /* navigation pending */
      });
    }

    throw error;
  }
}

// On page load: clean up the _reload cache-buster URL param.
// NOTE: We intentionally do NOT clear chunk reload markers here.
// Markers expire naturally via MARKER_EXPIRY_MS check in shouldAutoReload().
// The previous approach of clearing them on a 5-second timer was the root cause
// of infinite reload loops on slow mobile networks — the timer fired before
// lazy chunks had even started loading, resetting the counter to 0 every time.
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_reload')) {
      url.searchParams.delete('_reload');
      window.history.replaceState(window.history.state, document.title, url.toString());
    }
  });
}
